<script setup>
import { reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  setting: { type: Object, required: true },
  disabled: { type: Boolean, default: false },
  isSaving: { type: Boolean, default: false },
});

const emit = defineEmits(['save']);
const { t } = useI18n();

const form = reactive({
  enabled: false,
  api_key: '',
  endpoint: '',
  model: '',
  embedding_endpoint: '',
  embedding_api_key: '',
  embedding_model: '',
  allowed_tool_hosts: '',
});

// API Key 不回填，空值提交时由后端保留现有密钥。
watch(
  () => props.setting,
  value => {
    Object.assign(form, {
      enabled: Boolean(value.enabled),
      api_key: '',
      endpoint: value.endpoint || '',
      model: value.model || '',
      embedding_endpoint: value.embedding_endpoint || '',
      embedding_api_key: '',
      embedding_model: value.embedding_model || '',
      allowed_tool_hosts: (value.allowed_tool_hosts || []).join('\n'),
    });
  },
  { immediate: true, deep: true }
);

const submit = () => {
  const allowedToolHosts = form.allowed_tool_hosts
    .split(/\r?\n/)
    .map(host => host.trim())
    .filter(Boolean);

  emit('save', { ...form, allowed_tool_hosts: allowedToolHosts });
};
</script>

<template>
  <section class="border-b border-n-weak bg-n-background">
    <!-- 模型配置与下方助手侧栏共用列宽，形成稳定的后台工作区。 -->
    <div class="grid w-full lg:grid-cols-[17rem_minmax(0,1fr)]">
      <div
        class="flex flex-col gap-3 border-b border-n-weak px-4 py-5 md:px-6 lg:border-b-0 lg:border-r xl:px-8"
      >
        <div class="flex items-center justify-between gap-4">
          <h2 class="text-base font-medium text-n-slate-12">
            {{ t('JUYANG_AI.SETTINGS.TITLE') }}
          </h2>
          <div class="flex items-center gap-2">
            <span class="text-sm text-n-slate-11">
              {{
                form.enabled ? t('JUYANG_AI.ENABLED') : t('JUYANG_AI.DISABLED')
              }}
            </span>
            <Switch v-model="form.enabled" :disabled="disabled" />
          </div>
        </div>
        <p class="text-sm text-n-slate-11">
          {{ t('JUYANG_AI.SETTINGS.DESCRIPTION') }}
        </p>
      </div>

      <form
        class="grid min-w-0 gap-4 px-4 py-5 md:grid-cols-2 md:px-6 xl:grid-cols-12 xl:px-8"
        @submit.prevent="submit"
      >
        <div class="min-w-0 xl:col-span-5">
          <Input
            v-model="form.endpoint"
            :label="t('JUYANG_AI.SETTINGS.ENDPOINT')"
            :disabled="disabled"
            required
          />
        </div>
        <div class="min-w-0 xl:col-span-3">
          <Input
            v-model="form.api_key"
            type="password"
            autocomplete="new-password"
            :label="t('JUYANG_AI.SETTINGS.API_KEY')"
            :placeholder="t('JUYANG_AI.SETTINGS.API_KEY_PLACEHOLDER')"
            :disabled="disabled"
          />
        </div>
        <div class="min-w-0 xl:col-span-4">
          <Input
            v-model="form.model"
            :label="t('JUYANG_AI.SETTINGS.MODEL')"
            :disabled="disabled"
            required
          />
        </div>
        <div class="min-w-0 xl:col-span-5">
          <Input
            v-model="form.embedding_endpoint"
            :label="t('JUYANG_AI.SETTINGS.EMBEDDING_ENDPOINT')"
            :placeholder="
              t('JUYANG_AI.SETTINGS.EMBEDDING_ENDPOINT_PLACEHOLDER')
            "
            :disabled="disabled"
          />
        </div>
        <div class="min-w-0 xl:col-span-3">
          <Input
            v-model="form.embedding_api_key"
            type="password"
            autocomplete="new-password"
            :label="t('JUYANG_AI.SETTINGS.EMBEDDING_API_KEY')"
            :placeholder="t('JUYANG_AI.SETTINGS.EMBEDDING_API_KEY_PLACEHOLDER')"
            :disabled="disabled"
          />
        </div>
        <div class="min-w-0 xl:col-span-4">
          <Input
            v-model="form.embedding_model"
            :label="t('JUYANG_AI.SETTINGS.EMBEDDING_MODEL')"
            :disabled="disabled"
            required
          />
        </div>
        <label
          class="flex min-w-0 flex-col gap-1 text-sm font-medium text-n-slate-12 md:col-span-2 xl:col-span-9"
        >
          {{ t('JUYANG_AI.SETTINGS.ALLOWED_TOOL_HOSTS') }}
          <textarea
            v-model="form.allowed_tool_hosts"
            class="reset-base min-h-20 w-full resize-y rounded-lg border-0 bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-brand"
            :placeholder="
              t('JUYANG_AI.SETTINGS.ALLOWED_TOOL_HOSTS_PLACEHOLDER')
            "
            :disabled="disabled"
          />
        </label>
        <Button
          v-if="!disabled"
          class="justify-self-end self-end md:col-span-2 xl:col-span-3"
          type="submit"
          icon="i-lucide-save"
          :label="isSaving ? t('JUYANG_AI.SAVING') : t('JUYANG_AI.SAVE')"
          :is-loading="isSaving"
          :disabled="isSaving"
        />
      </form>
    </div>
  </section>
</template>
